import React, { useRef, useState, useEffect, useCallback } from "react";

export default function ResizableSidebar({
  children,
  min = 280,
  max = 1500,
  initial = 310,
}) {
  const [width, setWidth] = useState(() => Math.max(min, Math.min(max, initial)));
  const containerRef = useRef(null);

  const dragging = useRef(false);
  const dragStartX = useRef(0);
  const dragStartW = useRef(width);

  const clamp = (v) => Math.max(min, Math.min(max, v));

  // --- resize with deltas so it doesn't jump
  const onDragStart = useCallback((e) => {
    const pt = e.touches ? e.touches[0] : e;
    dragging.current = true;
    dragStartX.current = pt.clientX;
    dragStartW.current = width;

    e.stopPropagation();
    e.preventDefault();

    const move = (ev) => {
      if (!dragging.current) return;
      const p = ev.touches ? ev.touches[0] : ev;
      const delta = p.clientX - dragStartX.current; // left-anchored sidebar
      setWidth((_) => clamp(dragStartW.current + delta));
      ev.stopPropagation();
      ev.preventDefault();
    };

    const end = () => {
      dragging.current = false;
      window.removeEventListener("mousemove", move, true);
      window.removeEventListener("mouseup", end, true);
      window.removeEventListener("touchmove", move, { capture: true });
      window.removeEventListener("touchend", end, { capture: true });
    };

    window.addEventListener("mousemove", move, true);
    window.addEventListener("mouseup", end, true);
    window.addEventListener("touchmove", move, { capture: true, passive: false });
    window.addEventListener("touchend", end, { capture: true });
  }, [min, max, width]);

  // --- wheel/touch: allow native scrolling but keep events out of the stage
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const onWheelCapture = (e) => {
      // Let the browser scroll the sidebar; just don't let the wheel bubble to the stage/canvas.
      e.stopPropagation();
      // DO NOT preventDefault() here — keeps native scrolling working.
    };

    const onTouchStartCapture = (e) => {
      // Start of a scroll gesture — don't leak it to the stage.
      e.stopPropagation();
    };

    const onTouchMoveCapture = (e) => {
      // Allow native scroll (no preventDefault), but keep it from reaching the stage.
      e.stopPropagation();
    };

    el.addEventListener("wheel", onWheelCapture, { capture: true, passive: true });
    el.addEventListener("touchstart", onTouchStartCapture, { capture: true, passive: true });
    el.addEventListener("touchmove", onTouchMoveCapture, { capture: true, passive: true });

    return () => {
      el.removeEventListener("wheel", onWheelCapture, { capture: true });
      el.removeEventListener("touchstart", onTouchStartCapture, { capture: true });
      el.removeEventListener("touchmove", onTouchMoveCapture, { capture: true });
    };
  }, []);

  return (
    <div
      ref={containerRef}
      style={{
        width,
        padding: 8,
        background: "#111",
        color: "white",
        boxSizing: "border-box",
        borderRight: "1px solid #1f2937",
        position: "relative",
        userSelect: dragging.current ? "none" : "auto",

        // scrolling + containment so it doesn't chain to the page/stage
        overflowY: "auto",
        overflowX: "hidden",
        height: "100%",
        minHeight: 0,
        overscrollBehavior: "contain",
        WebkitOverflowScrolling: "touch",
        touchAction: "pan-y",
      }}
    >
      <div
        onMouseDown={onDragStart}
        onTouchStart={onDragStart}
        style={{
          position: "absolute",
          right: 0,
          top: 0,
          bottom: 0,
          width: 8,
          cursor: "col-resize",
          background: "transparent",
          zIndex: 10,
          touchAction: "none",
        }}
        title="Drag to resize"
      />
      {children}
    </div>
  );
}
